import React, { FC, ReactNode } from 'react'
import { Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { ContractorTransfer, ContractorTransferStatusEnum } from 'src/api'
import { StatefulDialog } from 'src/components/ui'
import { canCancelTransfer, canRetryTransfer, getTransferQueuedMessage } from 'src/components/columns/contractorTransferHelpers'
import { useCancelContractorTransferRequest, useRetryContractorTransferRequest } from 'src/hooks/api'
import { useAddPopupMessage, useDialog } from 'src/hooks/ui'
import { DialogNames, Entities } from 'src/enums'

interface ConfirmDialogProps {
  name: DialogNames
  title: string
  text: ReactNode
  confirmLabel: string
  confirmColor: 'primary' | 'error'
  isLoading: boolean
  onConfirm: () => void
}

const ConfirmDialog: FC<ConfirmDialogProps> = ({ name, title, text, confirmLabel, confirmColor, isLoading, onConfirm }) => {
  const { closeDialog } = useDialog(name)

  return (
    <StatefulDialog name={name} wrapContent={false}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box width="400px" maxWidth="100%">
          <Typography>{text}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => closeDialog()}>
          Cancel
        </Button>
        <Button variant="contained" color={confirmColor} disabled={isLoading} onClick={onConfirm}>
          {isLoading ? <CircularProgress color="info" size={26} /> : confirmLabel}
        </Button>
      </DialogActions>
    </StatefulDialog>
  )
}

interface ContractorTransferActionsProps {
  transfer: ContractorTransfer
  onRetried?: () => void
}

/** Retry and Cancel, shown only where the backend allows them. */
export const ContractorTransferActions: FC<ContractorTransferActionsProps> = ({ transfer, onRetried }) => {
  const queryClient = useQueryClient()
  const addMessage = useAddPopupMessage()
  const retryDialog = useDialog(DialogNames.RetryContractorTransfer)
  const cancelDialog = useDialog(DialogNames.CancelContractorTransfer)

  const onSettled = (): void => {
    queryClient.invalidateQueries([Entities.ContractorTransfers])
  }

  const retry = useRetryContractorTransferRequest({
    options: {
      onSuccess: (retried) => {
        addMessage(getTransferQueuedMessage(retried, true))
        onRetried?.()
        retryDialog.closeDialog()
      },
      onError: ({ message: text }) => addMessage({ text, type: 'error' }),
      onSettled
    }
  })

  const cancel = useCancelContractorTransferRequest({
    options: {
      onSuccess: () => {
        addMessage({ text: 'The transfer was cancelled', type: 'success' })
        cancelDialog.closeDialog()
      },
      onError: ({ message: text }) => addMessage({ text, type: 'error' }),
      onSettled
    }
  })

  const showRetry = canRetryTransfer(transfer)
  const showCancel = canCancelTransfer(transfer)

  if (!showRetry && !showCancel) return null

  const retryText =
    transfer.status === ContractorTransferStatusEnum.CompletedWithWarnings
      ? 'The heal and the verification run again for the rows left with the source contractor.'
      : 'The job resumes from the stage that failed. Nothing that already moved is moved twice.'

  return (
    <Grid container spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
      {showCancel && (
        <Grid item>
          <Button variant="outlined" color="error" onClick={cancelDialog.openDialog}>
            Cancel transfer
          </Button>
        </Grid>
      )}
      {showRetry && (
        <Grid item>
          <Button variant="contained" onClick={retryDialog.openDialog}>
            Retry
          </Button>
        </Grid>
      )}
      <ConfirmDialog
        name={DialogNames.RetryContractorTransfer}
        title="Retry the transfer"
        text={retryText}
        confirmLabel="Retry"
        confirmColor="primary"
        isLoading={retry.isLoading}
        onConfirm={() => retry.mutate(transfer.id)}
      />
      <ConfirmDialog
        name={DialogNames.CancelContractorTransfer}
        title="Cancel the transfer"
        text="Nothing has moved yet. The transfer stops and its projects, users and devices stay with the source contractor."
        confirmLabel="Cancel transfer"
        confirmColor="error"
        isLoading={cancel.isLoading}
        onConfirm={() => cancel.mutate(transfer.id)}
      />
    </Grid>
  )
}
